import React from 'react';
import GlowingOrb from '../GlowingOrb';

/** Props for {@link CampaignAskLauncher}. */
export interface CampaignAskLauncherProps {
  /** Placeholder shown in the faux composer. */
  placeholder?: string;
  /** Open the focused ask modal. */
  onOpen: () => void;
  /** `card` reads as a text box; `button` is a small pill for the Overview's Ask hollie card. */
  variant?: 'card' | 'button';
}

/**
 * Send-arrow icon: identical to the one on the wizard composer
 * (`ObjectiveChatInput`) so the send affordance reads the same everywhere.
 */
const SendIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l14-7-7 14-2-5-5-2z" />
  </svg>
);

/**
 * The inline ask card in the sticky sidebar (above Responses on a campaign,
 * above the Guest card on a single conversation). The whole card is the
 * affordance: clicking anywhere opens the focused ask modal where the
 * conversation actually happens. It is a launcher, not a live composer, so a
 * stray keystroke can never send from a cramped inline field.
 *
 * It is styled to read as an actual text box: a bordered white card (matching
 * its sidebar neighbours) with the orb inside, the prompt, and the same round
 * volt send button the wizard composer uses. One compact line; clicking it
 * focuses the real composer in the modal.
 */
export const CampaignAskLauncher: React.FC<CampaignAskLauncherProps> = ({
  placeholder = 'Ask hollie',
  onOpen,
  variant = 'card',
}) => variant === 'button' ? (
  <button
    type="button"
    onClick={onOpen}
    aria-label="Ask hollie"
    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-volt px-3 text-sm font-medium text-accent-foreground transition-colors hover:bg-volt-dark focus-visible:outline-2 focus-visible:outline-ring"
  >
    <SendIcon className="h-3.5 w-3.5" />
    Ask
  </button>
) : (
  <button
    type="button"
    onClick={onOpen}
    aria-label="Ask hollie"
    className="group flex w-full items-center gap-2.5 rounded-2xl border border-border/50 bg-background/50 px-3 py-2 text-left transition-colors hover:border-volt/40 focus:outline-none focus-visible:border-volt"
  >
    <div className="h-8 w-8 shrink-0">
      <GlowingOrb state="listening" form="orb" className="h-full w-full" />
    </div>
    <span className="min-w-0 flex-1 truncate text-base text-muted-foreground">{placeholder}</span>
    <span
      data-testid="ask-send"
      aria-hidden="true"
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-volt text-accent-foreground transition-colors group-hover:bg-volt-dark"
    >
      <SendIcon className="h-4 w-4" />
    </span>
  </button>
);
